import { useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { addNegativeBrandsFromText, addNegativeProductsFromText } from '@/utils/spAdGroupCreate';
import type { SpAddedNegativeBrand, SpAddedNegativeProduct } from '@/types/spCampaignCreate';
import { SpAdGroupSection } from '@/components/campaign-create/create-adgroup/SpAdGroupSection';

type ProductTab = 'brands' | 'products';

export function NegativeProductTargetingSection({
  negativeProducts,
  onProductsChange,
  negativeBrands,
  onBrandsChange,
  disabled,
}: {
  negativeProducts: SpAddedNegativeProduct[];
  onProductsChange: (rows: SpAddedNegativeProduct[]) => void;
  negativeBrands: SpAddedNegativeBrand[];
  onBrandsChange: (rows: SpAddedNegativeBrand[]) => void;
  disabled?: boolean;
}) {
  const [tab, setTab] = useState<ProductTab>('brands');
  const [text, setText] = useState('');
  const addedCount = negativeBrands.length + negativeProducts.length;

  const addTyped = () => {
    if (!text.trim() || disabled) return;
    if (tab === 'brands') {
      onBrandsChange(addNegativeBrandsFromText(text, negativeBrands).brands);
    } else {
      onProductsChange(addNegativeProductsFromText(text, negativeProducts).products);
    }
    setText('');
  };

  return (
    <SpAdGroupSection title="Negative product targeting" optional collapsible defaultOpen={false}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-border rounded-xl overflow-hidden">
        <div className="p-4 space-y-3 bg-surface">
          <div className="flex gap-1 text-sm border-b border-border">
            {(['brands', 'products'] as const).map((id) => (
              <button
                key={id}
                type="button"
                className={cn(
                  'px-3 py-2 -mb-px border-b-2',
                  tab === id
                    ? 'border-brand text-brand font-medium'
                    : 'border-transparent text-foreground-muted'
                )}
                onClick={() => setTab(id)}
                disabled={disabled}
              >
                {id === 'brands' ? 'Exclude brands' : 'Exclude products'}
              </button>
            ))}
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                addTyped();
              }
            }}
            placeholder={
              tab === 'brands' ? 'Enter brand names, one per line' : 'Enter ASINs, one per line'
            }
            className="w-full min-h-[140px] border border-border rounded-lg px-3 py-2 text-sm"
            disabled={disabled}
            aria-label={tab === 'brands' ? 'Brand names to exclude' : 'ASINs to exclude'}
          />
          <button
            type="button"
            className="text-sm px-3 py-1.5 rounded-lg border border-border text-foreground disabled:opacity-40"
            disabled={disabled || !text.trim()}
            onClick={addTyped}
          >
            {tab === 'brands' ? 'Add brands' : 'Add products'}
          </button>
        </div>
        <div className="p-4 bg-page/60 border-t lg:border-t-0 lg:border-l border-border">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium">{addedCount} added</p>
            {addedCount > 0 ? (
              <button
                type="button"
                className="text-sm text-brand"
                disabled={disabled}
                onClick={() => {
                  onBrandsChange([]);
                  onProductsChange([]);
                }}
              >
                Remove all
              </button>
            ) : null}
          </div>
          <p className="text-xs font-medium text-foreground-muted uppercase tracking-wide mb-2">
            Brands & products
          </p>
          <ul className="text-sm space-y-1">
            {negativeBrands.map((row) => (
              <li key={`brand:${row.brand}`} className="flex items-center gap-2">
                <span className="flex-1 truncate">{row.brand}</span>
                <span className="text-xs text-foreground-muted">Brand</span>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onBrandsChange(negativeBrands.filter((item) => item.brand !== row.brand))}
                  aria-label="Remove excluded brand"
                >
                  <X className="w-4 h-4 text-foreground-muted" />
                </button>
              </li>
            ))}
            {negativeProducts.map((row) => (
              <li key={`asin:${row.asin}`} className="flex items-center gap-2">
                <span className="flex-1 truncate">{row.asin}</span>
                <span className="text-xs text-foreground-muted">Product</span>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() =>
                    onProductsChange(negativeProducts.filter((item) => item.asin !== row.asin))
                  }
                  aria-label="Remove excluded product"
                >
                  <X className="w-4 h-4 text-foreground-muted" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </SpAdGroupSection>
  );
}
